import type { RefCallback } from "react";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import FolderZipOutlinedIcon from "@mui/icons-material/FolderZipOutlined";
import InsertPhotoOutlinedIcon from "@mui/icons-material/InsertPhotoOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import { Controller } from "react-hook-form";
import type { Control, FieldErrors } from "react-hook-form";
import type { TFunction } from "i18next";
import DefaultInput from "@/components/forms/inputs/defaultInput";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import {
  getLmsCourseLanguageLabel,
  getLmsCourseImageViewLink,
  hasLmsCourseVariantScormPackage,
  type LmsCourseEditorFormValues,
  type LmsCourseLanguageVariant,
} from "@/components/lms/lms.utils";
import { getImageUrl } from "@/helper/imageUrlHandler";
import styles from "@/routes/lms/lmsCourseOverview.module.css";

interface Props {
  canEditVariant: boolean;
  canUploadFeaturedImage: boolean;
  canUploadScorm: boolean;
  control: Control<LmsCourseEditorFormValues>;
  courseIsActive: boolean;
  disabled: boolean;
  errors: FieldErrors<LmsCourseEditorFormValues>;
  onDownloadFeaturedImage: (variant: LmsCourseLanguageVariant) => void;
  onDownloadScorm: (variant: LmsCourseLanguageVariant) => void;
  onPreviewScorm?: (variant: LmsCourseLanguageVariant) => void;
  onUploadFeaturedImage: (variant: LmsCourseLanguageVariant) => void;
  onUploadScorm: (variant: LmsCourseLanguageVariant) => void;
  onViewFeaturedImage: (variant: LmsCourseLanguageVariant) => void;
  registerValidationTarget: (
    fieldName:
      | `variants.${number}.durationInMinutes`
      | `variants.${number}.isActive`
  ) => RefCallback<HTMLElement>;
  t: TFunction;
  variant: LmsCourseLanguageVariant;
  variantIndex: number;
}

export default function LmsCourseVariantCard({
  canEditVariant,
  canUploadFeaturedImage,
  canUploadScorm,
  control,
  courseIsActive,
  disabled,
  errors,
  onDownloadFeaturedImage,
  onDownloadScorm,
  onPreviewScorm,
  onUploadFeaturedImage,
  onUploadScorm,
  onViewFeaturedImage,
  registerValidationTarget,
  t,
  variant,
  variantIndex,
}: Props) {
  const languageLabel = getLmsCourseLanguageLabel(variant.language, t);
  const durationFieldName =
    `variants.${variantIndex}.durationInMinutes` as const;
  const activeFieldName = `variants.${variantIndex}.isActive` as const;
  const variantErrors = errors.variants?.[variantIndex];
  const featuredImageDisplayName =
    variant.featured_image_display_name ?? variant.basename_of_featured_image;
  const hasFeaturedImage = Boolean(
    featuredImageDisplayName && variant.featured_image_view_link
  );
  const featuredImageThumbnailLink = hasFeaturedImage
    ? variant.featured_image_thumbnail_view_link ??
      variant.featured_image_view_link
    : null;
  const featuredImageThumbnailUrl = featuredImageThumbnailLink
    ? getImageUrl(getLmsCourseImageViewLink(featuredImageThumbnailLink, variant.featured_image_url))
    : null;
  const scormArchiveDisplayName =
    variant.scorm_archive_display_name ?? variant.basename_of_scorm_archive;
  const hasDownloadableScormFile = Boolean(
    scormArchiveDisplayName && variant.scorm_file_download_link
  );
  const hasScormPackage = hasLmsCourseVariantScormPackage(variant);

  return (
    <div
      className={styles.variantCard}
      data-testid={`course-variant-card-${variant.language ?? "new"}`}
    >
      <div className={styles.variantCardHeader}>
        <h3>{languageLabel}</h3>
      </div>

      <div className={styles.settingsGrid}>
        <div
          className={styles.fieldBlock}
          data-testid={`lms-course-variant-${variant.language ?? "new"}-duration-field`}
          ref={registerValidationTarget(durationFieldName)}
        >
          <Controller
            name={durationFieldName}
            control={control}
            rules={
              canEditVariant
                ? {
                    required: t("field_required"),
                    validate: (value) =>
                      Number(value) > 0 ||
                      t("field_must_be_greater_than_zero"),
                  }
                : undefined
            }
            render={({ field }) => (
              <>
                <div className={styles.fieldLabel}>
                  {t("duration_in_minutes")}
                  <span className={styles.requiredAsterisk}>*</span>
                </div>
                <DefaultInput
                  {...field}
                  type="number"
                  min={1}
                  disabled={!canEditVariant || disabled}
                />
                {variantErrors?.durationInMinutes?.message && (
                  <div className={styles.fieldErrorText}>
                    {variantErrors.durationInMinutes.message}
                  </div>
                )}
              </>
            )}
          />
        </div>

        <VariantBooleanToggleField
          disabled={!canEditVariant || disabled}
          control={control}
          errorMessage={
            variantErrors?.isActive?.message
              ? String(variantErrors.isActive.message)
              : undefined
          }
          fieldName={activeFieldName}
          forceInactive={!courseIsActive && !canEditVariant}
          label={t("active")}
          registerValidationTarget={registerValidationTarget(activeFieldName)}
          t={t}
          validate={
            canEditVariant
              ? (value) => {
                  if (!value) return true;
                  if (!courseIsActive) {
                    return t("variant_activation_requires_active_course");
                  }
                  return (
                    hasScormPackage ||
                    t("variant_activation_requires_scorm_file")
                  );
                }
              : undefined
          }
        />
      </div>

      <div className={styles.variantAssetGrid}>
        <div className={styles.variantAssetPanel}>
          <strong>{t("course_featured_image")}</strong>
          <div className={styles.variantAssetContent}>
            <div className={styles.variantAssetDetails}>
              {featuredImageThumbnailUrl && featuredImageDisplayName && (
                <Tooltip title={t("view")} placement="top" arrow>
                  <button
                    type="button"
                    className={styles.variantImageThumbnailButton}
                    aria-label={`${t("view")}: ${featuredImageDisplayName}`}
                    onClick={() => onViewFeaturedImage(variant)}
                    data-testid={`lms-course-variant-${variant.language ?? "new"}-featured-image-thumbnail`}
                  >
                    <img
                      src={featuredImageThumbnailUrl}
                      alt={featuredImageDisplayName}
                      className={styles.variantImageThumbnail}
                    />
                    <span
                      className={styles.variantImageThumbnailOverlay}
                      aria-hidden="true"
                    >
                      <span className={styles.variantImageThumbnailViewIcon}>
                        <VisibilityOutlinedIcon />
                      </span>
                    </span>
                  </button>
                </Tooltip>
              )}
              {!hasFeaturedImage && (
                <span className={styles.variantAssetEmptyState}>
                  {t("no_file_uploaded")}
                </span>
              )}
            </div>
            <div className={styles.variantAssetActions}>
              <GenericButton
                variant="outlined"
                disabled={!hasFeaturedImage}
                onClick={() => onDownloadFeaturedImage(variant)}
                startIcon={<FileDownloadOutlinedIcon />}
                data-testid={`lms-course-variant-${variant.language ?? "new"}-featured-image-download-button`}
              >
                {t("download")}
              </GenericButton>
              {canUploadFeaturedImage && variant.id && (
                <GenericButton
                  variant="outlined"
                  disabled={disabled}
                  onClick={() => onUploadFeaturedImage(variant)}
                  startIcon={<InsertPhotoOutlinedIcon />}
                  data-testid={`lms-course-variant-${variant.language ?? "new"}-featured-image-button`}
                >
                  {t(hasFeaturedImage ? "replace_featured_image" : "upload_featured_image")}
                </GenericButton>
              )}
            </div>
          </div>
        </div>

        <div className={styles.variantAssetPanel}>
          <strong>{t("scorm_file")}</strong>
          <div className={styles.variantAssetContent}>
            <div className={styles.variantAssetDetails}>
              {scormArchiveDisplayName && (
                <div
                  className={styles.variantScormFileIcon}
                  aria-hidden="true"
                >
                  <FolderZipOutlinedIcon />
                </div>
              )}
              {!scormArchiveDisplayName && (
                <span className={styles.variantAssetEmptyState}>
                  {t("no_file_uploaded")}
                </span>
              )}
            </div>
            <div className={styles.variantAssetActions}>
              {onPreviewScorm && (
                <GenericButton
                  variant="outlined"
                  disabled={disabled || !variant.id || !hasScormPackage}
                  onClick={() => onPreviewScorm(variant)}
                  startIcon={<PlayArrowOutlinedIcon />}
                  data-testid={`lms-course-variant-${variant.language ?? "new"}-scorm-preview-button`}
                >
                  {t("preview_scorm")}
                </GenericButton>
              )}
              <GenericButton
                variant="outlined"
                disabled={disabled || !hasDownloadableScormFile}
                onClick={() => onDownloadScorm(variant)}
                startIcon={<FileDownloadOutlinedIcon />}
              >
                {t("download")}
              </GenericButton>
              {canUploadScorm && variant.id && (
                <GenericButton
                  variant="outlined"
                  disabled={disabled}
                  onClick={() => onUploadScorm(variant)}
                  startIcon={<FolderZipOutlinedIcon />}
                  data-testid={`lms-course-variant-${variant.language ?? "new"}-scorm-button`}
                >
                  {t(hasScormPackage ? "replace_scorm_file" : "upload_scorm_file")}
                </GenericButton>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function VariantBooleanToggleField({
  control,
  disabled,
  errorMessage,
  fieldName,
  forceInactive,
  label,
  registerValidationTarget,
  t,
  validate,
}: {
  control: Control<LmsCourseEditorFormValues>;
  disabled: boolean;
  errorMessage?: string;
  fieldName: `variants.${number}.isActive`;
  forceInactive: boolean;
  label: string;
  registerValidationTarget: RefCallback<HTMLElement>;
  t: TFunction;
  validate?: (value: boolean) => true | string;
}) {
  return (
    <div className={styles.toggleFieldRow} ref={registerValidationTarget}>
      <div className={styles.fieldLabel}>{label}</div>
      <Controller
        name={fieldName}
        control={control}
        rules={validate ? { validate } : undefined}
        render={({ field }) => (
          <ToggleButtonGroup
            exclusive
            fullWidth
            color="secondary"
            data-testid={`lms-course-${fieldName}-toggle`}
            disabled={disabled}
            value={!forceInactive && field.value ? "yes" : "no"}
            onChange={(_event, value) => {
              if (!value) return;
              field.onChange(value === "yes");
            }}
          >
            <ToggleButton value="yes">{t("yes")}</ToggleButton>
            <ToggleButton value="no">{t("no")}</ToggleButton>
          </ToggleButtonGroup>
        )}
      />
      {errorMessage && (
        <div className={styles.fieldErrorText}>{errorMessage}</div>
      )}
    </div>
  );
}
